import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, unref as _unref, mergeProps as _mergeProps, toHandlers as _toHandlers, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { key: 1 }
const _hoisted_2 = { class: "flex flex-col gap-6 text-left" }
const _hoisted_3 = { class: "text-gray-600" }
const _hoisted_4 = /*#__PURE__*/_createElementVNode("strong", { class: "text-black" }, "Unidade:", -1)
const _hoisted_5 = /*#__PURE__*/_createElementVNode("p", { class: "font-bold text-base text-[#323232] text-left mb-4 mt-4" }, " Regras de proporcionalidade ", -1)
const _hoisted_6 = { class: "grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6" }
const _hoisted_7 = /*#__PURE__*/_createElementVNode("p", { class: "font-bold text-base text-[#323232] text-left mb-2 mt-4" }, " Lembrete de confirmação pelo aplicativo ", -1)
const _hoisted_8 = { class: "flex gap-2" }

import { ref, reactive, watch, onMounted, computed } from 'vue'
import { timezones } from '@/hooks/useCommon'
import SelectableCard from '@/components/designSystem/Cards/SelectableCard.vue'
import { useToast } from 'vue-toastification'
import Network from '@/Network'
import { useErrorToast } from '@/composables/useErrorToast'
import { mdiCalendarBlankOutline } from '@mdi/js'
import { DatePicker } from 'v-calendar'
import 'v-calendar/style.css'

import { ScheduleResponse, OperationalGroup } from '@/Network/Types/Responses/ScheduleUnitResponses'
import { ProportionalityRuleEnum } from '@/Network/Types/Requests/ScheduleRequests'
import Spinner from '@/components/others/Spinner.vue'
import { proportionalityRuleOptions } from '@/utils/schedule'
import OperationalGroupSelect from '@/components/designSystem/Select/OperationalGroupSelect.vue'
import FieldOptionsService from '@/Network/Services/FieldOptionsService'
import UnidentifiedCoverageScheduleCard from './UnidentifiedCoverageScheduleCard.vue'
import DisableUnidentifiedCoverageModal from './DisableUnidentifiedCoverageModal.vue'
import { scheduleSupportsCoverage } from '@/utils/coverage'
import AgendaTypeField from '@/components/ManageScale/Schedule/AgendaTypeField.vue'
import type {
  AgendaDetectionResponse,
  ScheduleAgendaTypeValue
} from '@/Network/Types/Responses/DemandResponses'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'
import { AuthorizationPermissions, AuthorizationResourcesAndSubjects } from '@/types/authorization'

type DisableDecision = { confirmed: boolean; releaseFuture: boolean }

export default /*#__PURE__*/_defineComponent({
  __name: 'ScheduleBasicInfoTab',
  props: {
    scheduleId: {}
  },
  emits: ["update:isSaving", "update:modelValue"],
  setup(__props: any, { expose: __expose, emit }) {

const props = __props;




const toast = useToast()
const { notifyError } = useErrorToast()
const isLoading = ref(false)
const schedule = ref<ScheduleResponse | null>(null)
const originalSnapshot = ref('')
// Ocupado sem identificação (M8-B): só aparece quando o BE já expõe o campo.
const coverageSupported = ref(false)
const originalAllowUnregistered = ref(false)
const disableModal = reactive({ open: false, future: 0, past: 0 })
let resolveDisableDecision: ((decision: DisableDecision) => void) | null = null

async function askDisableConfirmation(): Promise<DisableDecision> {
  let counts: { future: number; past: number } | null = null
  try {
    counts = await Network.schedule.getUnregisteredCoverageCount(local.id)
  } catch (err) {
    notifyError(err, 'Não foi possível contar os plantões ocupados sem identificação.')
    return { confirmed: false, releaseFuture: false }
  }
  // Sem plantões marcados: salva direto, sem confirmação.
  if (!counts || counts.future + counts.past === 0) return { confirmed: true, releaseFuture: false }
  disableModal.future = counts.future
  disableModal.past = counts.past
  disableModal.open = true
  return new Promise((resolve) => {
    resolveDisableDecision = resolve
  })
}

function settleDisable(decision: DisableDecision) {
  disableModal.open = false
  // Cancelou o desligar: o switch volta ao original (ligado), sem alteração pendente.
  if (!decision.confirmed) local.allowUnregisteredDoctor = originalAllowUnregistered.value
  resolveDisableDecision?.(decision)
  resolveDisableDecision = null
}

const { flags } = useFeatureFlags()
const agendaDetection = ref<AgendaDetectionResponse | null>(null)

/**
 * Alterar o tipo de agenda exige `schedule_editing` na escala (Q18). A store de
 * permissões só é consultada com a flag ligada — flag off não muda nada.
 */
const agendaTypeReadonly = computed(() => {
  if (!flags.enabledDashboardDemand) return false
  return !usePermissionsStore().check({
    permission: AuthorizationPermissions.ScheduleEditing,
    resource: { objectType: AuthorizationResourcesAndSubjects.Schedule, objectId: props.scheduleId }
  })
})

function getLocalSnapshot() {
  return JSON.stringify({
    ...(flags.enabledDashboardDemand && { agendaType: local.agendaType }),
    name: local.name,
    timezone: local.timezone,
    endDate: local.endDate,
    proportionalityRule: local.proportionalityRule,
    operationalGroup: local.operationalGroup?.name ?? null,
    teamDoctorReminderEnabled: local.teamDoctorReminderEnabled,
    externalDoctorReminderEnabled: local.externalDoctorReminderEnabled,
    allowUnregisteredDoctor: local.allowUnregisteredDoctor
  })
}

const hasUnsavedChanges = computed(() => {
  if (!originalSnapshot.value) return false
  return getLocalSnapshot() !== originalSnapshot.value
})

const local = reactive({
  id: 0,
  name: '',
  timezone: '',
  endDate: null as Date | string | null,
  healthUnit: { id: 0, name: '' },
  proportionalityRule: ProportionalityRuleEnum.PROPORTIONAL_WITH_LIMIT,
  positions: [],
  requiresDoctorPaymentFormation: false,
  operationalGroup: null as OperationalGroup | null,
  teamDoctorReminderEnabled: true,
  externalDoctorReminderEnabled: true,
  allowUnregisteredDoctor: false,
  agendaType: 'AUTO' as ScheduleAgendaTypeValue
})

const allOperationalGroups = ref<OperationalGroup[]>([])
const fieldOptionsService = new FieldOptionsService()

const operationalGroupOptions = computed(() => {
  return allOperationalGroups.value
})

const load = async () => {
  isLoading.value = true
  try {
    const [scheduleData, allGroupsData] = await Promise.all([
      Network.schedule.getById(props.scheduleId) as Promise<ScheduleResponse>,
      fieldOptionsService.getOperationalGroups()
    ])

    schedule.value = scheduleData
    allOperationalGroups.value = allGroupsData
    Object.assign(local, scheduleData)
    coverageSupported.value = scheduleSupportsCoverage(scheduleData)
    originalAllowUnregistered.value = scheduleData.allowUnregisteredDoctor === true
    local.allowUnregisteredDoctor = originalAllowUnregistered.value
    local.agendaType = scheduleData.agendaType ?? 'AUTO'

    if (scheduleData.operationalGroup) {
      local.operationalGroup =
        allGroupsData.find((g) => g.name === scheduleData.operationalGroup) ?? null
    }

    originalSnapshot.value = getLocalSnapshot()
  } catch (err) {
    notifyError(err, 'Não foi possível carregar os dados da escala.')
  } finally {
    isLoading.value = false
  }
}

/**
 * Detecção do tipo de agenda (só com a flag). Falha não bloqueia a aba: o campo
 * cai no texto "será detectado após 8 semanas".
 */
const loadAgendaDetection = async () => {
  if (!flags.enabledDashboardDemand) return
  try {
    agendaDetection.value = await Network.demand.agendaDetection(props.scheduleId)
  } catch (err) {
    console.error('Falha ao carregar a detecção do tipo de agenda', err)
    agendaDetection.value = null
  }
}

onMounted(() => {
  load()
  loadAgendaDetection()
})

watch(
  () => local,
  () => {
    emit('update:modelValue', { ...local })
  },
  { deep: true }
)

function validateBasicInfo(): boolean {
  if (!local.name.trim()) {
    toast.error('O nome da escala não pode ficar vazio.')
    return false
  }

  if (!local.timezone) {
    toast.error('Selecione um fuso horário.')
    return false
  }

  if (!local.proportionalityRule) {
    toast.error('Selecione uma regra de proporcionalidade.')
    return false
  }

  if (!local.operationalGroup) {
    toast.error('Selecione um Grupo Operacional.')
    return false
  }

  return true
}

// Desligar com plantões marcados pede confirmação (Q30-A); ligar ou manter salva direto.
async function resolveCoverageDecision(): Promise<DisableDecision> {
  const turningOff =
    coverageSupported.value && originalAllowUnregistered.value && !local.allowUnregisteredDoctor
  if (!turningOff) return { confirmed: true, releaseFuture: false }
  return askDisableConfirmation()
}

const handleSave = async () => {
  if (!validateBasicInfo()) return

  const decision = await resolveCoverageDecision()
  if (!decision.confirmed) return
  // Atalho desmarcado não viaja: só `true` é enviado.
  const releaseFutureUnregistered = decision.releaseFuture || undefined

  emit('update:isSaving', true)
  try {
    await Network.schedule.update(local.id, {
      name: local.name,
      timezone: local.timezone,
      endDate: local.endDate,
      proportionalityRule: local.proportionalityRule,
      operationalGroup: local.operationalGroup?.name ?? null,
      teamDoctorReminderEnabled: local.teamDoctorReminderEnabled,
      externalDoctorReminderEnabled: local.externalDoctorReminderEnabled,
      ...(coverageSupported.value
        ? { allowUnregisteredDoctor: local.allowUnregisteredDoctor }
        : {}),
      ...(releaseFutureUnregistered ? { releaseFutureUnregistered } : {}),
      // Flag off: payload idêntico ao de antes (sem a chave `agendaType`).
      ...(flags.enabledDashboardDemand && { agendaType: local.agendaType })
    })

    originalSnapshot.value = getLocalSnapshot()
    originalAllowUnregistered.value = local.allowUnregisteredDoctor
    toast.success('Informações básicas salvas com sucesso.')
  } catch (err: unknown) {
    // `Network.schedule.update` normaliza o erro em ServiceError, então
    // `axios.isAxiosError` deixou de casar e a mensagem do backend se perdia.
    notifyError(err, 'Não foi possível salvar as informações básicas da escala.')
  } finally {
    emit('update:isSaving', false)
  }
}

__expose({
  handleSave,
  hasUnsavedChanges
})

return (_ctx: any,_cache: any) => {
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_list_item = _resolveComponent("v-list-item")!
  const _component_v_autocomplete = _resolveComponent("v-autocomplete")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_checkbox = _resolveComponent("v-checkbox")!

  return (isLoading.value)
    ? (_openBlock(), _createBlock(Spinner, {
        key: 0,
        color: "primary",
        size: "5rem",
        width: "0.5rem",
        class: "absolute inset-0 flex justify-center items-center bg-white bg-opacity-50 z-50"
      }))
    : (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createVNode(_component_v_row, {
          dense: "",
          class: "gap-4"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_v_col, { cols: "12" }, {
              default: _withCtx(() => [
                _createVNode(_component_v_text_field, {
                  modelValue: local.name,
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((local.name) = $event)),
                  label: "Nome da escala",
                  "data-testid": "schedule-name-input",
                  variant: "outlined",
                  color: "primary",
                  "hide-details": ""
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_component_v_row, { dense: "" }, {
              default: _withCtx(() => [
                _createVNode(_component_v_col, {
                  cols: "12",
                  md: "8"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_autocomplete, {
                      modelValue: local.timezone,
                      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((local.timezone) = $event)),
                      label: "Fuso Horário",
                      "data-testid": "schedule-timezone-input",
                      items: _unref(timezones),
                      variant: "outlined",
                      color: "primary20",
                      "hide-details": ""
                    }, {
                      item: _withCtx(({ props, item }) => [
                        _createVNode(_component_v_list_item, _mergeProps(props, {
                          title: item.raw?.title,
                          subtitle: item.raw?.subtitle
                        }), null, 16, ["title", "subtitle"])
                      ]),
                      _: 1
                    }, 8, ["modelValue", "items"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_v_col, {
                  cols: "12",
                  md: "4"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(DatePicker), {
                      modelValue: local.endDate,
                      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((local.endDate) = $event)),
                      locale: "pt-BR",
                      masks: { input: `DD/MM/YYYY` }
                    }, {
                      default: _withCtx(({ inputValue, inputEvents }) => [
                        _createVNode(_component_v_text_field, _mergeProps({
                          label: "Fim da escala",
                          "data-testid": "schedule-end-date-input",
                          "append-inner-icon": _unref(mdiCalendarBlankOutline),
                          "model-value": inputValue
                        }, _toHandlers(inputEvents), {
                          color: "primary20",
                          variant: "outlined",
                          "hide-details": ""
                        }), null, 16, ["append-inner-icon", "model-value"])
                      ]),
                      _: 1
                    }, 8, ["modelValue"])
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }),
            _createVNode(_component_v_col, { cols: "12" }, {
              default: _withCtx(() => [
                _createVNode(OperationalGroupSelect, {
                  "model-value": local.operationalGroup,
                  "onUpdate:modelValue": _cache[3] || (_cache[3] = 
            (newValue) => {
              local.operationalGroup = newValue
            }
          ),
                  items: operationalGroupOptions.value,
                  "data-testid": "schedule-operational-group-input"
                }, null, 8, ["model-value", "items"])
              ]),
              _: 1
            }),
            _createElementVNode("div", _hoisted_2, [
              _createElementVNode("div", _hoisted_3, [
                _hoisted_4,
                _createTextVNode(" " + _toDisplayString(local.healthUnit.name), 1)
              ])
            ])
          ]),
          _: 1
        }),
        (_unref(flags).enabledDashboardDemand)
          ? (_openBlock(), _createBlock(AgendaTypeField, {
              key: 0,
              modelValue: local.agendaType,
              "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((local.agendaType) = $event)),
              mode: "edit",
              readonly: agendaTypeReadonly.value,
              detection: agendaDetection.value
            }, null, 8, ["modelValue", "readonly", "detection"]))
          : _createCommentVNode("", true),
        _hoisted_5,
        _createElementVNode("div", _hoisted_6, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(proportionalityRuleOptions), (opt) => {
            return (_openBlock(), _createBlock(SelectableCard, {
              key: opt.value,
              title: opt.title,
              description: opt.description,
              selected: local.proportionalityRule === opt.value,
              "data-testid": `proportionality-rule-${opt.value.toLowerCase().replace(/_/g, '-')}-card`,
              onClick: ($event: any) => (local.proportionalityRule = opt.value)
            }, null, 8, ["title", "description", "selected", "data-testid", "onClick"]))
          }), 128))
        ]),
        _hoisted_7,
        _createElementVNode("div", _hoisted_8, [
          _createVNode(_component_v_checkbox, {
            modelValue: local.teamDoctorReminderEnabled,
            "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((local.teamDoctorReminderEnabled) = $event)),
            "data-testid": "team-doctor-reminder-checkbox",
            label: "Lembrete para médico interno",
            density: "compact",
            color: "primary",
            "hide-details": "auto",
            disabled: true,
            class: "w-1/2"
          }, null, 8, ["modelValue"]),
          _createVNode(_component_v_checkbox, {
            modelValue: local.externalDoctorReminderEnabled,
            "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((local.externalDoctorReminderEnabled) = $event)),
            "data-testid": "external-doctor-reminder-checkbox",
            label: "Lembrete para médico externo",
            density: "compact",
            color: "primary",
            "hide-details": "auto",
            class: "w-1/2"
          }, null, 8, ["modelValue"])
        ]),
        (coverageSupported.value)
          ? (_openBlock(), _createBlock(UnidentifiedCoverageScheduleCard, {
              key: 1,
              modelValue: local.allowUnregisteredDoctor,
              "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((local.allowUnregisteredDoctor) = $event)),
              class: "mt-6"
            }, null, 8, ["modelValue"]))
          : _createCommentVNode("", true),
        _createVNode(DisableUnidentifiedCoverageModal, {
          "show-modal": disableModal.open,
          "future-count": disableModal.future,
          "past-count": disableModal.past,
          onConfirm: _cache[8] || (_cache[8] = (releaseFuture) => settleDisable({ confirmed: true, releaseFuture })),
          onCancel: _cache[9] || (_cache[9] = ($event: any) => (settleDisable({ confirmed: false, releaseFuture: false })))
        }, null, 8, ["show-modal", "future-count", "past-count"])
      ]))
}
}

})